import type { DocPoint } from "@productlane/lib"
import { DOC_POINT_TEXT_LENGTH } from "@productlane/lib"

interface InlineNode {
	nodeSize: number
	isTextblock: boolean
	type: { name: string }
	content: { size: number }
}

// Only the shape of a ProseMirror doc these helpers read, so tests need no editor.
interface Block {
	nodeSize: number
	textContent: string
	isTextblock?: boolean
	content?: { size: number }
	descendants?: (
		visit: (node: InlineNode, pos: number) => boolean | void,
	) => void
}

interface BlockDoc {
	childCount: number
	child: (index: number) => Block
}

interface Line {
	start: number
	end: number
}

/**
 * The lines inside a block, as offsets from the block's start: each paragraph of
 * a list and each stretch between line breaks. A teammate's copy that is a few
 * letters behind then keeps their caret on its own line, not the next one.
 */
function linesOf(block: Block): Line[] {
	const lines: Line[] =
		block.isTextblock && block.content
			? [{ start: 1, end: 1 + block.content.size }]
			: []
	block.descendants?.((node, pos) => {
		if (node.isTextblock) {
			lines.push({ start: pos + 2, end: pos + 2 + node.content.size })
			return true
		}
		if (node.type.name === "hardBreak") {
			const current = lines[lines.length - 1]
			if (current) {
				const breakAt = pos + 1
				lines.push({ start: breakAt + node.nodeSize, end: current.end })
				current.end = breakAt
			}
		}
		return true
	})
	return lines
}

// How far from the sent block number a matching block is looked for.
const SEARCH_RADIUS = 3

function blockStart(doc: BlockDoc, index: number): number {
	let start = 0
	for (let i = 0; i < index; i += 1) start += doc.child(i).nodeSize
	return start
}

function inBlock(doc: BlockDoc, block: number, offset: number): number {
	const inner = doc.child(block).nodeSize - 1
	return (
		blockStart(doc, block) +
		Math.max(Math.min(offset, inner), Math.min(1, inner))
	)
}

// The point inside a block: on its own line when both copies know the line,
// otherwise by the offset from the block's start.
function place(doc: BlockDoc, block: number, point: DocPoint): number {
	const lines = point.line === undefined ? [] : linesOf(doc.child(block))
	const line = lines[Math.min(point.line ?? 0, lines.length - 1)]
	if (!line) return inBlock(doc, block, point.offset)
	const offset = Math.min(line.start + (point.lineOffset ?? 0), line.end)
	return blockStart(doc, block) + Math.max(offset, line.start)
}

const opening = (doc: BlockDoc, block: number) =>
	doc.child(block).textContent.slice(0, DOC_POINT_TEXT_LENGTH)

/** The top-level block a position sits in, how far into it, and how it and the block above open. */
export function pointAt(doc: BlockDoc, pos: number): DocPoint {
	let start = 0
	for (let block = 0; block < doc.childCount; block += 1) {
		const size = doc.child(block).nodeSize
		if (pos < start + size || block === doc.childCount - 1) {
			const offset = Math.max(0, pos - start)
			const lines = linesOf(doc.child(block))
			let line = -1
			lines.forEach((l, index) => {
				if (offset >= l.start) line = index
			})
			return {
				block,
				offset,
				text: opening(doc, block),
				blocks: doc.childCount,
				before: block > 0 ? opening(doc, block - 1) : "",
				...(line >= 0
					? { line, lineOffset: Math.max(0, offset - lines[line]!.start) }
					: {}),
			}
		}
		start += size
	}
	return { block: 0, offset: 0, text: "", blocks: 0, before: "" }
}

// The sender's text, also while this copy holds fewer of its letters.
function sameText(text: string, sent: string): boolean {
	if (sent === "" || text === "") return sent === text
	return text.startsWith(sent) || sent.startsWith(text)
}

// The block and the one above it both read like the sender's, so an empty or
// one-letter line is never matched to a lookalike elsewhere in the doc.
function sameBlock(doc: BlockDoc, block: number, point: DocPoint): boolean {
	if (!sameText(opening(doc, block), point.text ?? "")) return false
	if (point.before === undefined) return true
	return block === 0
		? point.block === 0
		: sameText(opening(doc, block - 1), point.before)
}

const DISTINCT_LENGTH = 3

function distinctMatch(text: string, sent: string): boolean {
	return (
		Math.min(text.length, sent.length) >= DISTINCT_LENGTH &&
		sameText(text, sent)
	)
}

/** The nearest block to `home` that passes `match`. */
function nearest(
	doc: BlockDoc,
	home: number,
	match: (block: number) => boolean,
): number | null {
	const last = doc.childCount - 1
	for (let distance = 0; distance <= SEARCH_RADIUS; distance += 1) {
		const candidates =
			distance === 0 ? [home] : [home - distance, home + distance]
		for (const block of candidates) {
			if (block >= 0 && block <= last && match(block)) return block
		}
	}
	return null
}

const endOf = (doc: BlockDoc, block: number) =>
	inBlock(doc, block, doc.child(block).nodeSize)

/**
 * The position for a peer's point in this reader's copy, kept inside the same
 * block, so text the reader has not received yet never pushes it into the next one.
 */
export function positionOf(doc: BlockDoc, point: DocPoint): number {
	if (doc.childCount === 0) return 0
	const last = doc.childCount - 1
	const home = Math.min(point.block, last)
	// Same block count: the numbers line up, even while a block's text differs.
	if (point.text === undefined || point.blocks === doc.childCount) {
		return place(doc, home, point)
	}
	const block =
		nearest(doc, home, (b) => sameBlock(doc, b, point)) ??
		// The line above may be the one still on its way; a distinctive opening
		// alone is enough, a short or empty one never is.
		nearest(doc, home, (b) => distinctMatch(opening(doc, b), point.text ?? ""))
	if (block !== null) return place(doc, block, point)
	if (point.block === 0) return inBlock(doc, 0, 0)
	// Their line has not reached this copy yet: wait at the end of the line above it.
	const above =
		point.before === undefined
			? null
			: nearest(doc, Math.min(point.block - 1, last), (b) =>
					sameText(opening(doc, b), point.before ?? ""),
				)
	return endOf(doc, above ?? Math.min(point.block, doc.childCount) - 1)
}
